import { Button, Input, Label, Spinner } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { Banknote, Download, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { CursorPagination, INITIAL_PAGINATION, type PaginationState, registerNextPageCursor } from '@/components/data-table/cursor-pagination';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { FUNDS_TRANSACTIONS_SEARCH_QUERY_KEY, useTransactionsSearch } from '@/hooks/queries/funds/use-transactions';
import { useAsyncFileExport } from '@/hooks/use-async-file-export';
import { getErrorMessage } from '@/lib/api/error';
import { buildTransactionsSearchRequest, type Transaction, type TransactionFilters } from '@/services/api/funds-transactions';
import { ManualTransactionDialog } from './manual-transaction-dialog';
import { TRANSACTION_TABLE_COLUMNS } from './transaction-columns';
import { TransactionDetailDialog } from './transaction-detail-dialog';
import { TransactionFiltersRow } from './transaction-filters';

// processFileDownload can run up to 10 minutes; there is no failure push, so the
// client stops waiting itself (deliberate fix over legacy, which spun forever)
const DOWNLOAD_TIMEOUT_MS = 10 * 60 * 1000;

interface DownloadHistoryButtonProps {
  accountHolderId: string;
}

const DownloadHistoryButton = ({ accountHolderId }: DownloadHistoryButtonProps) => {
  const { isExporting, requestExport } = useAsyncFileExport('transactions', { accountHolder: accountHolderId, exportType: 'TRANSACTIONS' }, { timeoutMs: DOWNLOAD_TIMEOUT_MS });

  return (
    <div>
      <Button className="gap-2" disabled={isExporting} onClick={requestExport} type="button">
        {isExporting ? <Spinner className="size-4" /> : <Download className="size-4" />}
        Download Complete Transactions History
      </Button>
    </div>
  );
};
DownloadHistoryButton.displayName = 'DownloadHistoryButton';

interface AppliedDateRange {
  from?: string;
  to?: string;
}

export interface TransactionsPanelProps {
  /** Restricts every query to this account holder (user-page embed) */
  accountHolderId?: string;
  /** Shows the async CSV export button; requires accountHolderId (the export is per-user) */
  enableDownload?: boolean;
}

export const TransactionsPanel = ({ accountHolderId, enableDownload = false }: TransactionsPanelProps) => {
  const dateFieldId = useId();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<TransactionFilters>({});
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [appliedRange, setAppliedRange] = useState<AppliedDateRange>({});
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isManualDialogOpen, setIsManualDialogOpen] = useState(false);

  const request = useMemo(
    () =>
      buildTransactionsSearchRequest({
        filters,
        fromDate: appliedRange.from,
        toDate: appliedRange.to,
        cursor: pagination.cursors[pagination.currentPage],
        accountHolderId,
      }),
    [filters, appliedRange, pagination, accountHolderId],
  );

  const query = useTransactionsSearch(request);

  useEffect(() => {
    const nextCursor = query.data?.cursor;
    if (!nextCursor) return;
    setPagination((previous) => registerNextPageCursor(previous, nextCursor));
  }, [query.data]);

  const invalidateTransactions = useCallback(() => queryClient.invalidateQueries({ queryKey: FUNDS_TRANSACTIONS_SEARCH_QUERY_KEY }), [queryClient]);

  const handleApplyDates = () => {
    const nextFrom = draftFrom || undefined;
    const nextTo = draftTo || undefined;
    // Page 1 has no cursor, so an unchanged range on page 1 leaves the query key
    // identical — refetch explicitly in that case
    const isSameKey = appliedRange.from === nextFrom && appliedRange.to === nextTo && pagination.currentPage === 1;
    setAppliedRange({ from: nextFrom, to: nextTo });
    setPagination(INITIAL_PAGINATION);
    if (isSameKey) void query.refetch();
  };

  // TableFilters hands back the whole next filter map on every chip apply/Clear
  // All, so this replaces filters wholesale rather than diffing per field.
  const handleFiltersChange = (next: TransactionFilters) => {
    setFilters(next);
    setPagination(INITIAL_PAGINATION);
  };

  const handlePageChange = (page: number) => setPagination((previous) => ({ ...previous, currentPage: page }));

  return (
    <div className="flex flex-col gap-4">
      {/* The export needs an account holder server-side ('Account holder is required') —
          never offered without one */}
      {enableDownload && accountHolderId ? <DownloadHistoryButton accountHolderId={accountHolderId} /> : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${dateFieldId}-from`}>Date From</Label>
          <Input className="w-44" id={`${dateFieldId}-from`} onChange={(event) => setDraftFrom(event.target.value)} type="date" value={draftFrom} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${dateFieldId}-to`}>Date To</Label>
          <Input className="w-44" id={`${dateFieldId}-to`} onChange={(event) => setDraftTo(event.target.value)} type="date" value={draftTo} />
        </div>
        <Button aria-label="Update" onClick={handleApplyDates} size="icon" type="button">
          <RefreshCw className="size-4" />
        </Button>
      </div>

      <CursorPagination ariaLabel="Transaction pages" currentPage={pagination.currentPage} onPageChange={handlePageChange} totalPages={pagination.totalPages} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <TransactionFiltersRow filters={filters} isAccountHolderLocked={Boolean(accountHolderId)} onChange={handleFiltersChange} />
        <Button className="gap-2" onClick={() => setIsManualDialogOpen(true)} type="button">
          <Banknote className="size-4" />
          Create Manual Transaction
        </Button>
      </div>

      {query.isError ? (
        <QueryErrorBanner message={`Failed to fetch transactions: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} />
      ) : (
        // Deliberate fix: controls above stay mounted; only the table
        // skeletons during fetches. Empty and error states did not exist in legacy.
        <DataTable
          columns={TRANSACTION_TABLE_COLUMNS}
          data={query.data?.transactions}
          emptyMessage="No results"
          getRowId={(row) => row.transaction_id}
          isLoading={query.isFetching}
          onRowClick={setSelectedTransaction}
        />
      )}

      {selectedTransaction ? <TransactionDetailDialog onClose={() => setSelectedTransaction(null)} onMutated={invalidateTransactions} transaction={selectedTransaction} /> : null}
      {isManualDialogOpen ? <ManualTransactionDialog onClose={() => setIsManualDialogOpen(false)} onCreated={invalidateTransactions} /> : null}
    </div>
  );
};
TransactionsPanel.displayName = 'TransactionsPanel';
